const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';


const API = {
  BASE_URL: `${BASE_URL}/api`,

  // Auth
  LOGIN: '/auth/login',
  REFRESH: '/auth/refresh',
  LOGOUT: '/auth/logout',
  CHANGE_PASSWORD: '/auth/change-password',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  PROFILE_ME: '/auth/me',

  // Institution self-registration (public — no token)
  REGISTRATION_PLANS: '/registration/plans',
  REGISTRATION_DEFAULT_PLAN: '/registration/default-plan',
  REGISTRATION_INSTITUTIONS: '/registration/institutions',
  REGISTRATION_SHORT_CODE: (code: string) =>
    `/registration/short-code-available?code=${encodeURIComponent(code)}`,
  REGISTRATION_EMAIL: (email: string) =>
    `/registration/email-available?email=${encodeURIComponent(email)}`,
  REGISTRATION_TRIAL_ELIGIBILITY: (email: string, planId?: string) =>
    `/registration/free-trial-eligibility?email=${encodeURIComponent(email)}${planId ? `&planId=${planId}` : ''}`,
  UPLOAD_REGISTRATION_LOGO: '/upload/registration-logo',

  // Institutions
  INSTITUTIONS: '/institutions',
  DASHBOARD_STATS: '/institutions/dashboard/stats',

  // Subscriptions
  SUBSCRIPTION_PLANS: '/subscriptions/plans',
  SUBSCRIPTION_PLAN: (id: string) => `/subscriptions/plans/${id}`,
  SUBSCRIPTION_PLANS_AVAILABLE: '/subscriptions/plans/available',
  SUBSCRIPTION_MINE: '/subscriptions/my-subscription',
  SUBSCRIPTION_RENEW: '/subscriptions/renew',
  SUBSCRIPTION_EXPIRE_SWEEP: '/subscriptions/expire-sweep',
  FREE_TRIAL_START: '/subscriptions/free-trial/start',
  FREE_TRIAL_USAGE: '/subscriptions/free-trial/usage',
  FREE_TRIAL_ELIGIBILITY: (planId?: string) =>
    `/subscriptions/free-trial/eligibility${planId ? `?planId=${planId}` : ''}`,
  SUBSCRIPTION_ASSIGN: (institutionId: string) => `/subscriptions/assign/${institutionId}`,

  DASHBOARD_TRENDS: '/institutions/dashboard/participant-trends',
  DASHBOARD_EVENT_STATUS: '/institutions/dashboard/event-status',
  SUPERADMIN_STATS: '/institutions/dashboard/superadmin-stats',

  // Users
  USERS: '/users',

  // Fests
  FESTS: '/fests',
  FEST_OVERVIEW: (id: string) => `/fests/${id}/overview`,
  FEST_LOCK: (id: string) => `/fests/${id}/lock`,
  FEST_UNLOCK: (id: string) => `/fests/${id}/unlock`,
  FEST_CHANGE_STATUS: (id: string) => `/fests/${id}/status`,

  // Groups
  GROUPS: '/groups',

  TEAMS: '/teams',
  TEAMS_BULK: '/teams/bulk',
  TEAM_MINE: '/teams/mine',
  TEAM_MY_ASSIGNMENTS: '/teams/my-assignments',
  TEAM_USER_ASSIGNMENTS: '/teams/assignments',
  TEAM_DETAILS: (id: string) => `/teams/${id}/details`,
  TEAM_ASSIGN_LEAD: (id: string) => `/teams/${id}/assign-team-lead`,
  TEAM_REMOVE_LEAD: (id: string) => `/teams/${id}/team-lead`,
  TEAM_REMOVE_USER_ASSIGNMENT: (teamId: string, userId: string) => `/teams/${teamId}/assignment/${userId}`,
  TEAM_AVAILABLE_LEADS: (id: string) => `/teams/${id}/team-leads`,

  // Volunteers
  VOLUNTEERS: '/volunteers',
  VOLUNTEERS_ASSIGN: '/volunteers/assign',
  VOLUNTEER_ASSIGNMENTS: '/volunteers/assignments',
  VOLUNTEER_MY_ASSIGNMENTS: '/volunteers/assigned-events',
  ANNOUNCEMENTS: '/volunteers/announcements',
  VOLUNTEER_TASKS: '/volunteers/tasks',
  VOLUNTEER_EVENT_TASKS: (eventId: string) => `/volunteers/events/${eventId}/tasks`,

  // Categories
  CATEGORIES_BULK: '/categories/bulk',
  CATEGORIES_AVAILABLE: '/categories/available',

  // Events
  EVENTS: '/events',
  EVENTS_BULK_UPLOAD: '/events/bulk-upload',
  EVENTS_BULK_PREVIEW: '/events/bulk-upload/preview',
  EVENTS_BULK_DELETE: '/events/bulk',
  COORDINATOR_STATS: '/events/coordinator/stats',
  CATEGORIES: '/categories',
  EVENT_PUBLISH: (id: string) => `/events/${id}/publish`,
  EVENT_CANCEL: (id: string) => `/events/${id}/cancel`,
  EVENT_LOCK: (id: string) => `/events/${id}/lock`,
  EVENT_UNLOCK: (id: string) => `/events/${id}/unlock`,
  EVENT_COMPLETE: (id: string) => `/events/${id}/complete`,
  EVENT_POSTER_TEMPLATES: (id: string) => `/events/${id}/poster-templates`,

  // Participants
  PARTICIPANTS: '/participants',
  PARTICIPANT_EVENTS: '/participant-events',
  PARTICIPANTS_BULK_DELETE: '/participants/bulk',
  PARTICIPANTS_BULK_IMPORT: '/participants/bulk-import',
  PARTICIPANTS_BULK_PREVIEW: '/participants/bulk-import/preview',
  PARTICIPANT_UPDATE_EVENT: '/participants/update-participant-event',
  PARTICIPANT_GENERATE_CODES: '/participants/generate-codes',
  PARTICIPANT_GENERATE_GROUP_CODES: '/participants/generate-group-codes',
  PARTICIPANT_SHUFFLE_CODES: '/participants/shuffle-codes',
  PARTICIPANT_SHUFFLE_GROUP_CODES: '/participants/shuffle-group-codes',
  PARTICIPANT_SCORE_ENTRY: (eventId: string) => `/participants/score-entry/${eventId}`,
  PARTICIPANT_SCORE_PROGRESS: (eventId: string) => `/participants/score-progress/${eventId}`,
  PARTICIPANT_MANUAL_SCORE: '/participants/manual-score',
  PARTICIPANT_MARK_ABSENT: '/participants/mark-absent',
  PARTICIPANT_MARK_PENDING: '/participants/mark-pending',
  PARTICIPANT_REGISTER_EVENT: (id: string) => `/participants/${id}/register-event`,
  PARTICIPANT_UNREGISTER_EVENT: (id: string, eventId: string) => `/participants/${id}/unregister-event/${eventId}`,
  PARTICIPANT_SET_CAPTAIN: (id: string) => `/participants/${id}/set-captain`,
  PARTICIPANT_SET_GROUP_CODE: '/participants/set-group-code',
  RESULT_PROGRESS: (eventId: string) => `/results/event/${eventId}/progress`,

  // Group Scoring
  GROUP_SCORE_ENTRY: (eventId: string) => `/scoring/group-entry/${eventId}`,
  GROUP_MANUAL_SCORE: '/scoring/group-marks',
  GROUP_MARK_ABSENT: '/scoring/group-absent',
  GROUP_MARK_PENDING: '/scoring/group-pending',
  GROUP_SCORE_PROGRESS: (eventId: string) => `/scoring/group-progress/${eventId}`,
  JUDGE_GROUP_SCORE: '/judges/group-score',
  JUDGE_GROUP_SCORES: (eventId: string) => `/judges/group-scores/${eventId}`,
  JUDGE_ADMIN_SCORE: '/judges/admin-score',
  JUDGE_ADMIN_SCORE_CLEAR: '/judges/admin-score/clear',
  JUDGE_ADMIN_GROUP_SCORE: '/judges/admin-group-score',
  JUDGE_ADMIN_GROUP_SCORE_CLEAR: '/judges/admin-group-score/clear',
  JUDGE_SCORES: (eventId: string) => `/judges/scores/${eventId}`,

  // Venues
  VENUES: '/venues',
  VENUES_BULK: '/venues/bulk',

  // Schedules
  SCHEDULES: '/venues/schedule',
  EVENTS_VENUE_REPORT: '/events/venue-report',
  EVENTS_SCHEDULE_VIEW: '/events/schedule-view',

  // Judge Assignments
  JUDGE_ASSIGN: '/judges/assign',
  JUDGE_ASSIGNMENTS: '/judges/assignments',
  JUDGE_FESTS: '/judges/fests',

  // Scores
  SCORES: '/scores',

  // Results
  RESULTS: '/results',
  RESULTS_CALCULATE: '/results/calculate',
  RESULTS_PUBLISH: '/results/publish',
  RESULT_EVENT: (eventId: string) => `/results/event/${eventId}`,
  RESULT_PUBLISH_EVENT: (eventId: string) => `/results/event/${eventId}/publish`,
  RESULT_UNPUBLISH_EVENT: (eventId: string) => `/results/event/${eventId}/unpublish`,
  RESULT_TOGGLE_ANNOUNCED: (eventId: string) => `/results/event/${eventId}/toggle-announced`,
  CHAMPIONSHIP: '/results/championship',
  BEST_PERFORMER: '/results/best-performer',
  CHAMPIONSHIP_REPORT: '/results/championship-report',

  // Certificates
  CERTIFICATES: '/certificates',
  CERTIFICATES_GENERATE: '/certificates/generate',
  CERTIFICATE_TEMPLATES: '/certificate-templates',
  INSTITUTION_CERTIFICATES: '/institution-certificates',
  CERTIFICATE_CHECK_TEMPLATE: (eventId: string) => `/certificates/check-template/${eventId}`,
  CERTIFICATE_ELIGIBLE_PARTICIPANTS: (eventId: string) => `/certificates/eligible-participants/${eventId}`,
  CERTIFICATE_GENERATE_EVENT: (eventId: string) => `/certificates/generate-event/${eventId}`,
  CERTIFICATE_GENERATE_PARTICIPANT: (participantId: string, eventId: string) => `/certificates/generate/${participantId}/event/${eventId}`,
  CERTIFICATE_REVERT_PARTICIPANT: (participantId: string, eventId: string) => `/certificates/revert-participant/${participantId}/event/${eventId}`,
  UPLOAD_CERTIFICATE_BG: '/upload/certificate-bg',
  UPLOAD_PARTICIPANT_PHOTO: '/upload/participant-photo',
  UPLOAD_LOGO: '/upload/logo',
  UPLOAD_PROFILE_PHOTO: '/upload/profile-photo',

  // Posters
  POSTERS: '/posters',
  POSTERS_GENERATED: '/posters/generated',
  POSTERS_DELETE_BEST_PERFORMER: (festId: string) => `/posters/generated/best-performer/${festId}`,
  POSTERS_DELETE_EVENT: (eventId: string) => `/posters/generated/event/${eventId}`,
  POSTERS_GENERATE: '/posters/generate-event',
  POSTERS_GENERATE_FEST: '/posters/generate-fest',
  POSTERS_GENERATE_CHAMPIONSHIP: '/posters/generate-championship',
  POSTERS_GENERATE_CHAMPIONSHIP_LEADERBOARD: '/posters/generate-championship-leaderboard',
  POSTERS_GENERATE_CHAMPIONSHIP_FINAL: '/posters/generate-championship-final',
  POSTERS_PREVIEW: '/posters/preview',
  POSTERS_PREVIEW_CHAMPIONSHIP_LEADERBOARD: (festId: string) => `/posters/preview-championship-leaderboard/${festId}`,
  POSTERS_PREVIEW_CHAMPIONSHIP_FINAL: (festId: string) => `/posters/preview-championship-final/${festId}`,
  POSTERS_GENERATE_CHAMPIONSHIP_BEST_PERFORMER: '/posters/generate-championship-best-performer',
  POSTERS_PREVIEW_CHAMPIONSHIP_BEST_PERFORMER: (festId: string) => `/posters/preview-championship-best-performer/${festId}`,
  POSTERS_GENERATE_ASYNC: '/posters/generate-async',
  POSTERS_REGENERATE: '/posters/regenerate',
  POSTERS_DOWNLOAD_IMAGE: (id: string) => `/posters/generated/${id}/download`,
  POSTER_TEMPLATES: '/poster-templates',
  INSTITUTION_POSTERS: '/institution-posters',
  UPLOAD_POSTER_BG: '/upload/poster-bg',


  // User Assignments (scope-based)
  USER_ASSIGNMENTS: '/user-assignments',
  USER_ASSIGNMENTS_MY: '/user-assignments/my',
  USER_ASSIGNMENTS_FEST: (festId: string, role?: string) => `/user-assignments/fest/${festId}${role ? `?role=${role}` : ''}`,
  USER_ASSIGNMENTS_EVENT: (eventId: string) => `/user-assignments/event/${eventId}`,

  // Audit Logs
  AUDIT_LOGS: '/audit-logs',

  // Roles & Permissions
  ROLES: '/admin/roles',

  // Public
  PUBLIC_RESULTS: '/public/results',
  PUBLIC_VERIFY: '/public/verify',
  PUBLIC_EVENTS:       '/public/events',
  PUBLIC_FESTS:        '/public/fests',
  PUBLIC_CERTIFICATE:  '/public/certificate',
  PUBLIC_FEST_RESULTS_BY_CODE: (festCode: string) => `/public/fests/by-code/${festCode}/results`,
  PUBLIC_EVENT_POSTERS: (eventId: string) => `/public/events/${eventId}/posters`,
  PUBLIC_EVENT_CERTIFICATES: (eventId: string) => `/public/events/${eventId}/certificates`,
};

export default API;
